import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchNonReferralData } from '../../services/leadManagementApi';
import axios from 'axios';
import { withRetry } from '../../lib/utils/withRetry';
import { withLmaSafeApiLogging } from '../../lib/utils/lmaSafeLogging';
import { LMADropdownResponse } from '../../components/LMA/types';

const objectToQueryString = (obj: any) => {
  const keys = Object.keys(obj);
  const keyValuePairs = keys.map((key) => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
  });
  return keyValuePairs.join('&');
};

export interface NonReferralsState {
  data: any[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  currentPage: number;
  pageSize: number;
  filters: Record<string, string>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dropdownData: LMADropdownResponse | null;
  dropdownLoading: boolean;
  dropdownError: string | null;
}

const initialState: NonReferralsState = {
  data: [],
  totalRecords: 0,
  loading: false,
  error: null,
  lastUpdated: null,
  currentPage: 1,
  pageSize: 10,
  filters: {},
  sortBy: undefined,
  sortOrder: undefined,
  dropdownData: null,
  dropdownLoading: false,
  dropdownError: null,
};

// Async thunk for fetching non-referrals data with filters and pagination
export const fetchNonReferrals = createAsyncThunk(
  'nonReferrals/fetchNonReferrals',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchNonReferralData(data.params, data.payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load non-referrals data. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching dropdown data for non-referrals tab
export const fetchNonReferralsDropdownData = createAsyncThunk(
  'nonReferrals/fetchDropdownData',
  async (leadStatusFilters: any[], { rejectWithValue, dispatch }) => {
    try {
      let hotLeadMin = 0;
      let coldLeadMax = 0;

      if (leadStatusFilters && Array.isArray(leadStatusFilters)) {
        leadStatusFilters.forEach((filter: any) => {
          const name = filter.name?.toLowerCase();
          const lowerLimit = filter.fields?.LSFDaysLowerLimit?.value;
          const upperLimit = filter.fields?.LSFDaysUpperLimit?.value;

          if (name && lowerLimit !== undefined) {
            if (name === 'hot' && upperLimit !== undefined) {
              hotLeadMin = lowerLimit;
            } else if (name === 'cold') {
              coldLeadMax = upperLimit;
            }
          }
        });
      }

      const params = {
        LeadStatus: 'requested',
        HotLeadMin: hotLeadMin,
        ColdLeadMax: coldLeadMax,
      };

      const response = await withLmaSafeApiLogging(
        {
          operation: 'fetchNonReferralsDropdownData',
          method: 'GET',
          endpoint: '/LMA/GetLMANonReferralFilterDropdownValues',
        },
        () =>
          withRetry(() =>
            axios.get(
              `/api/dap-api?url=/LMA/GetLMANonReferralFilterDropdownValues&${objectToQueryString(
                params
              )}`
            )
          )
      );
      return response?.data as LMADropdownResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load filter options. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

const nonReferralsSlice = createSlice({
  name: 'nonReferrals',
  initialState,
  reducers: {
    updateNonReferralRecord: (state, action: PayloadAction<{ id: string; updates: any }>) => {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...action.payload.updates };
      }
    },
    deleteNonReferralRecord: (state, action: PayloadAction<string>) => {
      const initialLength = state.data.length;
      state.data = state.data.filter((item) => item.id !== action.payload);
      const removed = initialLength - state.data.length;
      if (removed > 0) {
        state.totalRecords = Math.max(0, state.totalRecords - removed);
      }
    },
    deleteNonReferralRecordByPatientId: (state, action: PayloadAction<string>) => {
      const initialLength = state.data.length;
      state.data = state.data.filter((item) => item.patientId !== action.payload);
      const removed = initialLength - state.data.length;
      if (removed > 0) {
        state.totalRecords = Math.max(0, state.totalRecords - removed);
      }
    },
    addNonReferralRecord: (state, action: PayloadAction<any>) => {
      state.data.push(action.payload);
    },
    setFilters: (state, action: PayloadAction<Record<string, string>>) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to page 1 when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to page 1 when page size changes
    },
    setSorting: (state, action: PayloadAction<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    clearNonReferralsData: (state) => {
      state.data = [];
      state.totalRecords = 0;
      state.error = null;
      state.lastUpdated = null;
      state.filters = {};
      state.currentPage = 1;
      state.sortBy = undefined;
      state.sortOrder = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNonReferrals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNonReferrals.fulfilled, (state, action) => {
        state.loading = false;
        const rawData = action.payload.data || [];
        state.data = rawData.map((item: any) => {
          if (!item.deduplication && item.deduplicationJson) {
            try {
              const parsed = JSON.parse(item.deduplicationJson);
              return { ...item, deduplication: Array.isArray(parsed) ? parsed : [] };
            } catch {
              return item;
            }
          }
          return item;
        });
        state.totalRecords = action.payload.totalRecords || 0;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchNonReferrals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNonReferralsDropdownData.pending, (state) => {
        state.dropdownLoading = true;
        state.dropdownError = null;
      })
      .addCase(fetchNonReferralsDropdownData.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownData = action.payload;
      })
      .addCase(fetchNonReferralsDropdownData.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownError = action.payload as string;
      });
  },
});

export const {
  updateNonReferralRecord,
  deleteNonReferralRecord,
  addNonReferralRecord,
  deleteNonReferralRecordByPatientId,
  setFilters,
  setPage,
  setPageSize,
  setSorting,
  clearNonReferralsData,
} = nonReferralsSlice.actions;

export default nonReferralsSlice.reducer;
